import { KEYWORD } from '../api/types';
import type { Email, EmailAddress } from '../api/types';
import { AVATAR_COLORS } from '../theme/tokens';

export function displayName(list: EmailAddress[] | null | undefined): string {
    const first = list?.[0];
    if (!first) return '(unknown)';
    return first.name?.trim() || first.email;
}

export function addressOf(list: EmailAddress[] | null | undefined): string {
    return list?.[0]?.email ?? '';
}

export function initials(name: string): string {
    return name
        .split(/[\s@._-]+/)
        .filter(Boolean)
        .map((w) => w[0] ?? '')
        .slice(0, 2)
        .join('')
        .toUpperCase();
}

/** Stable per-sender colour so the same person keeps the same avatar. */
export function avatarColor(seed: string): string {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    return AVATAR_COLORS[h % AVATAR_COLORS.length] ?? 'var(--accent)';
}

/*
    Dates go through Intl rather than a hand-written month table, so a Turkish
    reader gets "10 Ağu" instead of "10 Aug". The locale is passed in rather than
    read from a store: these are pure, and the caller already knows the language.
*/
const cache = new Map<string, Intl.DateTimeFormat>();
const formatter = (locale: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
    const key = locale + JSON.stringify(opts);
    let f = cache.get(key);
    if (!f) {
        f = new Intl.DateTimeFormat(locale, opts);
        cache.set(key, f);
    }
    return f;
};

export function shortDate(iso: string | null, locale = 'en-GB'): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
        return formatter(locale, { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
    }
    if (d.getFullYear() === now.getFullYear()) {
        return formatter(locale, { day: '2-digit', month: 'short' }).format(d);
    }
    return formatter(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
}

export function fullDate(iso: string | null, locale = 'en-GB'): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return formatter(locale, {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(d);
}

export function fileSize(n: number, locale = 'en-GB'): string {
    const round = (v: number) => v.toLocaleString(locale, { maximumFractionDigits: 1 });
    if (n < 1024) return `${round(n)} B`;
    if (n < 1024 * 1024) return `${round(n / 1024)} KB`;
    return `${round(n / 1024 / 1024)} MB`;
}

/*
    Read, starred, draft — asked the way everything else asks.

    These used to spell the keywords themselves, with a leading dollar: `$seen`, `$flagged`,
    `$draft`. That is JMAP's spelling, and this client stopped speaking JMAP — the server sends
    `seen`, and every other part of the interface uses the KEYWORD constants. So the names never
    met: nothing ever had a `$seen` keyword, `isUnread` was therefore always true, and the message
    list drew every message as unread no matter how many times it had been opened. Stars and draft
    badges never appeared at all.

    One word with two spellings, again. The constants exist so there is one.
*/
export const isUnread = (e: Email) => !e.keywords[KEYWORD.seen];
export const isFlagged = (e: Email) => !!e.keywords[KEYWORD.flagged];
export const isDraft = (e: Email) => !!e.keywords[KEYWORD.draft];

/*
    Tidies the one-line preview the server generates.

    Stalwart builds it from whatever body part it finds, and a marketing mail is
    mostly Outlook conditional comments and a stylesheet — so the row reads
    `<!--[if mso]><!--> Keep track of your account …`, which looks broken. It
    also arrives full of the newlines and runs of spaces the HTML swallowed.
    Nothing here decodes or renders anything: it strips markup and collapses
    whitespace, so what is left is the sentence a person would have read.
*/
export function snippet(raw: string | null | undefined): string {
    if (!raw) return '';
    return raw
        .replace(/<!--[\s\S]*?(?:-->|$)/g, ' ')
        // A stray `<![endif]>` or a half-closed tag survives the comment sweep.
        .replace(/<!\[[^\]]*\]>/g, ' ')
        // A tag name, not just anything in angle brackets — plenty of senders
        // write a bare <https://example.com>, and eating it would delete the one
        // useful thing in the line.
        .replace(/<\/?[a-zA-Z][a-zA-Z0-9-]*(?:\s[^>]*)?\/?>/g, ' ')
        // Newsletters pad with non-breaking and zero-width spaces so the preview
        // a mail client picks up looks longer than it is. The class below holds
        // U+00A0, U+200B, U+200C, U+200D and U+FEFF — invisible here on purpose.
        .replace(/&nbsp;|&#160;|&zwnj;|[ ​‌‍﻿]/g, " ")
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;|&apos;/g, "'")
        .replace(/\s+/g, ' ')
        .trim();
}

/*
    Whether a message may not be from where it says, read from the Authentication-Results header
    the receiving server stamps on delivery.

    The reader used to draw the verdicts themselves — a green "Verified" pill when SPF, DKIM and
    DMARC passed, three coloured SPF/DKIM/DMARC badges when one did not. Neither told a person
    anything they could act on: nearly every legitimate message passes, so the pill was wallpaper,
    and a fraudster's look-alike domain passes too, so "Verified" was read as "safe" when it only
    meant "sent from the domain it names". The acronyms meant nothing to anybody.

    What IS worth saying is the one case where the From address is probably forged: DMARC failed.
    Where the domain publishes no DMARC verdict, the same conclusion needs SPF to fail outright and
    DKIM not to pass — SPF alone fails on ordinary forwarding and mailing lists, and would cry wolf.
*/
export function spoofSuspected(header: string | null | undefined): { suspect: boolean; detail: string } {
    if (!header) return { suspect: false, detail: '' };
    const verdict: Record<string, string> = {};
    for (const k of ['spf', 'dkim', 'dmarc']) {
        const m = new RegExp(`\\b${k}=(\\w+)`).exec(header);
        if (m?.[1]) verdict[k] = m[1].toLowerCase();
    }
    const dmarcSaid = verdict.dmarc === 'pass' || verdict.dmarc === 'fail';
    const suspect = verdict.dmarc === 'fail' || (!dmarcSaid && verdict.spf === 'fail' && verdict.dkim !== 'pass');
    const detail = Object.entries(verdict)
        .map(([k, v]) => `${k}: ${v}`)
        .join(' · ');
    return { suspect, detail };
}
